<script lang="ts">
	import SvelteSeo from 'svelte-seo';
	import { createPageSEO, createWebPageSchema, createFAQSchema } from '$lib/config/seo';
	import { page } from '$app/stores';
	import {
		Infinity as InfinityIcon,
		DollarSign,
		Users,
		Briefcase,
		TrendingUp,
		Shield,
		Zap,
		FileText,
		CheckCircle2
	} from '@lucide/svelte';
	import GlopayCtaHorizontal from '$lib/components/misc/glopay-cta-inline/glopay-cta-inline.svelte';

	const faqs = [
		{
			question: 'Will this always be free?',
			answer:
				"Yes. We're committed to keeping TalentProfile free forever for both developers and employers. Our business model doesn't require charging users."
		},
		{
			question: 'Are there any hidden fees?',
			answer:
				'No. Zero fees, ever. No per-hire charges, no subscription costs, no premium tiers. What you see is what you get - completely free.'
		},
		{
			question: 'How do you make money?',
			answer:
				"We're currently focused on building the best matching platform possible. We may explore optional premium features in the future, but the core matching service will always remain free."
		},
		{
			question: 'Is there a limit to how many jobs I can post?',
			answer:
				'No limits. Post as many jobs as you need. Receive unlimited candidate matches. No restrictions.'
		},
		{
			question: 'Do I need to provide credit card information?',
			answer:
				"No. We don't collect payment information at all. Just create an account and start posting jobs or uploading your CV."
		}
	];

	const seo = createPageSEO({
		title: 'Pricing - Free Forever | TalentProfile',
		description:
			'TalentProfile is completely free for both developers and employers. No subscription fees, no per-hire charges, no hidden costs. AI-powered matching at zero cost.',
		canonical: '/pricing',
		ogImage: '/pricing.png',
		structuredData: [
			createWebPageSchema({
				name: 'Pricing',
				description: 'TalentProfile pricing - free forever for developers and employers',
				url: '/pricing'
			}),
			createFAQSchema(faqs)
		]
	});

	const developerFeatures = [
		'Unlimited CV uploads and updates',
		'AI-powered job matching based on GitHub',
		'Email alerts for relevant positions',
		'Direct company contact',
		'Profile analytics and match scores',
		'Portfolio showcasing',
		'No spam, only relevant matches'
	];

	const employerFeatures = [
		'Unlimited job postings',
		'AI-powered candidate matching',
		'GitHub code analysis for all candidates',
		'Detailed match scores and reasoning',
		'Direct candidate contact',
		'No per-hire fees or commissions',
		'No seat limits or user restrictions'
	];

	const traditionalCosts = [
		{
			service: 'Recruiting Agency',
			cost: '$15,000 - $25,000',
			detail: 'Per hire (15-25% of first-year salary)',
			annual: '$60,000 - $100,000+ for 4 hires'
		},
		{
			service: 'Traditional ATS Software',
			cost: '$6,000 - $12,000',
			detail: 'Annual subscription for basic features',
			annual: '$6,000 - $12,000'
		},
		{
			service: 'Job Board Postings',
			cost: '$400 - $600',
			detail: 'Per job post on Indeed, LinkedIn, etc.',
			annual: '$4,800 - $7,200 for 12 posts'
		},
		{
			service: 'LinkedIn Recruiter',
			cost: '$8,000 - $10,000',
			detail: 'Annual license per recruiter',
			annual: '$8,000 - $10,000'
		}
	];

	const whyFree = [
		{
			icon: TrendingUp,
			title: 'Technology Scales Infinitely',
			description:
				'Our AI analysis costs pennies per match. Once built, the system serves millions of users at minimal marginal cost.'
		},
		{
			icon: Users,
			title: 'Network Effects',
			description:
				'More free users means better matches for everyone. Charging would limit our network and reduce match quality.'
		},
		{
			icon: Shield,
			title: 'Mission-Driven',
			description:
				'We believe great developers should be matched with great opportunities without financial barriers. Better matches benefit society.'
		},
		{
			icon: Zap,
			title: 'Long-Term Thinking',
			description:
				"We're building for decades, not quarters. Free access creates the strongest foundation for lasting impact."
		}
	];

	const comparison = [
		{
			feature: 'Job Postings',
			traditional: '$400-600 per post',
			talentProfile: 'Unlimited, free'
		},
		{
			feature: 'Candidate Matching',
			traditional: 'Manual keyword search',
			talentProfile: 'AI-powered GitHub analysis'
		},
		{
			feature: 'Success Fees',
			traditional: '15-25% of salary',
			talentProfile: '$0 per hire'
		},
		{
			feature: 'Monthly Subscription',
			traditional: '$500-1,000/month',
			talentProfile: '$0/month'
		},
		{
			feature: 'User Seats',
			traditional: '$100-300 per seat',
			talentProfile: 'Unlimited seats'
		},
		{
			feature: 'Technical Screening',
			traditional: 'Manual or extra cost',
			talentProfile: 'Automated, included'
		},
		{
			feature: 'Code Quality Analysis',
			traditional: 'Not available',
			talentProfile: 'Automated, included'
		},
		{
			feature: 'Setup Fees',
			traditional: '$500-2,000',
			talentProfile: '$0'
		}
	];
</script>

{#key $page.url.pathname}
	<SvelteSeo {...seo} />
{/key}

<div class="min-h-[calc(100dvh-527px)] bg-gradient-to-b from-white via-blue-50/30 to-white">
	<!-- Hero -->
	<section class="px-4 pt-20 pb-16">
		<div class="mx-auto max-w-5xl text-center">
			<div
				class="mb-6 inline-flex items-center gap-2 rounded-full bg-green-100 px-4 py-2 text-sm font-medium text-green-700"
			>
				<InfinityIcon class="h-4 w-4" />
				Free Forever
			</div>
			<h1 class="mb-6 text-4xl leading-tight font-bold text-gray-900 md:text-6xl">
				Pricing That Makes Sense:<br />
				<span class="text-blue-600">$0</span>
			</h1>
			<p class="mx-auto mb-8 max-w-3xl text-xl text-gray-600">
				No subscription fees. No per-hire charges. No hidden costs. TalentProfile is completely free
				for both developers and employers.
			</p>
		</div>
	</section>

	<!-- Main Pricing Cards -->
	<section class="px-4 py-16">
		<div class="mx-auto max-w-6xl">
			<div class="grid gap-8 md:grid-cols-2">
				<!-- For Developers -->
				<div class="relative overflow-hidden rounded-2xl border-2 border-blue-200 bg-white p-8">
					<div
						class="absolute top-0 right-0 -mt-16 -mr-16 h-32 w-32 rounded-bl-full bg-blue-50"
					></div>
					<Users class="mb-4 h-12 w-12 text-blue-600" />
					<h2 class="mb-2 text-2xl font-bold text-gray-900">For Developers</h2>
					<div class="mb-6 flex items-baseline gap-2">
						<span class="text-5xl font-bold text-blue-600">$0</span>
						<span class="text-gray-500">/forever</span>
					</div>
					<p class="mb-6 text-gray-600">
						Upload your CV, get matched with relevant jobs based on your GitHub profile. No cost,
						ever.
					</p>
					<ul class="mb-8 space-y-3">
						{#each developerFeatures as feature (feature)}
							<li class="flex items-start gap-2">
								<CheckCircle2 class="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
								<span class="text-gray-700">{feature}</span>
							</li>
						{/each}
					</ul>
					<a
						href="/"
						class="block w-full rounded-lg bg-blue-600 px-6 py-3 text-center font-semibold text-white transition-colors hover:bg-blue-700"
					>
						Upload Your CV
					</a>
				</div>

				<!-- For Employers -->
				<div class="relative overflow-hidden rounded-2xl border-2 border-green-200 bg-white p-8">
					<div
						class="absolute top-0 right-0 -mt-16 -mr-16 h-32 w-32 rounded-bl-full bg-green-50"
					></div>
					<Briefcase class="mb-4 h-12 w-12 text-green-600" />
					<h2 class="mb-2 text-2xl font-bold text-gray-900">For Companies</h2>
					<div class="mb-6 flex items-baseline gap-2">
						<span class="text-5xl font-bold text-green-600">$0</span>
						<span class="text-gray-500">/forever</span>
					</div>
					<p class="mb-6 text-gray-600">
						Post jobs, receive GitHub-verified candidates. No subscription, no per-hire fees.
					</p>
					<ul class="mb-8 space-y-3">
						{#each employerFeatures as feature (feature)}
							<li class="flex items-start gap-2">
								<CheckCircle2 class="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
								<span class="text-gray-700">{feature}</span>
							</li>
						{/each}
					</ul>
					<a
						href="/hire"
						class="block w-full rounded-lg bg-green-600 px-6 py-3 text-center font-semibold text-white transition-colors hover:bg-green-700"
					>
						Post a Job
					</a>
				</div>
			</div>
		</div>
	</section>

	<!-- What You Save -->
	<section class="bg-gray-50 px-4 py-20">
		<div class="mx-auto max-w-6xl">
			<h2 class="mb-4 text-center text-3xl font-bold text-gray-900 md:text-4xl">
				What Traditional Recruiting Actually Costs
			</h2>
			<p class="mx-auto mb-12 max-w-2xl text-center text-gray-600">
				Here's what companies typically spend on technical recruiting. With TalentProfile, all of
				this is $0.
			</p>
			<div class="grid gap-6 md:grid-cols-2">
				{#each traditionalCosts as item (item.service)}
					<div class="rounded-xl border border-gray-200 bg-white p-6">
						<div class="mb-3 flex items-start justify-between">
							<h3 class="text-lg font-bold text-gray-900">{item.service}</h3>
							<span class="text-2xl font-bold text-red-600">{item.cost}</span>
						</div>
						<p class="mb-2 text-sm text-gray-600">{item.detail}</p>
						<div class="border-t border-gray-100 pt-3">
							<p class="text-sm text-gray-500">
								Annual cost: <span class="font-semibold text-gray-900">{item.annual}</span>
							</p>
						</div>
					</div>
				{/each}
			</div>
			<div
				class="mt-8 rounded-xl border border-red-200 bg-gradient-to-r from-red-50 to-orange-50 p-8 text-center"
			>
				<p class="mb-2 text-sm text-gray-600">Total Annual Cost for Traditional Recruiting</p>
				<p class="mb-2 text-4xl font-bold text-red-600">$78,800 - $129,200+</p>
				<p class="text-gray-700">
					This is what companies typically spend per year hiring 4 developers
				</p>
			</div>
			<div
				class="mt-6 rounded-xl border border-green-200 bg-gradient-to-r from-green-50 to-blue-50 p-8 text-center"
			>
				<p class="mb-2 text-sm text-gray-600">Your Cost with TalentProfile</p>
				<p class="mb-2 text-5xl font-bold text-green-600">$0</p>
				<p class="text-gray-700">Forever. For unlimited hires.</p>
			</div>
		</div>
	</section>

	<!-- Comparison Table -->
	<section class="px-4 py-20">
		<div class="mx-auto max-w-5xl">
			<h2 class="mb-12 text-center text-3xl font-bold text-gray-900 md:text-4xl">
				Feature-by-Feature Comparison
			</h2>
			<div class="overflow-hidden rounded-xl border border-gray-200 bg-white">
				<table class="w-full">
					<thead>
						<tr class="border-b border-gray-200 bg-gray-50">
							<th class="px-6 py-4 text-left font-semibold text-gray-900">Feature</th>
							<th class="px-6 py-4 text-left font-semibold text-gray-900">Traditional ATS</th>
							<th class="px-6 py-4 text-left font-semibold text-blue-600">TalentProfile</th>
						</tr>
					</thead>
					<tbody>
						{#each comparison as row, i (row.feature)}
							<tr class="border-b border-gray-100 {i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}">
								<td class="px-6 py-4 font-medium text-gray-900">{row.feature}</td>
								<td class="px-6 py-4 text-gray-600">{row.traditional}</td>
								<td class="px-6 py-4 font-semibold text-blue-600">{row.talentProfile}</td>
							</tr>
						{/each}
					</tbody>
				</table>
			</div>
		</div>
	</section>

	<!-- Why Free -->
	<section class="bg-gray-50 px-4 py-20">
		<div class="mx-auto max-w-6xl">
			<h2 class="mb-4 text-center text-3xl font-bold text-gray-900 md:text-4xl">
				Why Is TalentProfile Free?
			</h2>
			<p class="mx-auto mb-12 max-w-2xl text-center text-gray-600">
				It's a fair question. Here's our honest answer.
			</p>
			<div class="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
				{#each whyFree as reason (reason.title)}
					<div
						class="rounded-xl border border-gray-200 bg-white p-6 transition-shadow hover:shadow-lg"
					>
						<div class="mb-4 w-fit superellipse bg-blue-100 p-3">
							<svelte:component this={reason.icon} class="h-6 w-6 text-blue-600" />
						</div>
						<h3 class="mb-3 text-lg font-bold text-gray-900">{reason.title}</h3>
						<p class="text-sm leading-relaxed text-gray-700">{reason.description}</p>
					</div>
				{/each}
			</div>
		</div>
	</section>

	<section class="bg-gray-50 px-4 py-8">
		<div class="mx-auto max-w-5xl">
			<GlopayCtaHorizontal placement="pricing-post-whyfree" />
		</div>
	</section>

	<!-- FAQ -->
	<section class="px-4 py-20">
		<div class="mx-auto max-w-4xl">
			<h2 class="mb-12 text-center text-3xl font-bold text-gray-900 md:text-4xl">
				Frequently Asked Questions
			</h2>
			<div class="space-y-6">
				{#each faqs as faq (faq.question)}
					<div class="rounded-xl border border-gray-200 bg-white p-6">
						<h3 class="mb-3 text-lg font-bold text-gray-900">{faq.question}</h3>
						<p class="leading-relaxed text-gray-700">{faq.answer}</p>
					</div>
				{/each}
			</div>
		</div>
	</section>

	<!-- CTA -->
	<section class="bg-gradient-to-r from-blue-600 to-green-600 px-4 py-20">
		<div class="mx-auto max-w-4xl text-center">
			<h2 class="mb-6 text-3xl font-bold text-white md:text-4xl">
				Ready to Start Hiring or Job Hunting?
			</h2>
			<p class="mx-auto mb-8 max-w-2xl text-xl text-blue-100">
				Join thousands of developers and companies using TalentProfile. No credit card required.
			</p>
			<div class="flex flex-col justify-center gap-4 sm:flex-row">
				<a
					href="/"
					class="inline-flex items-center justify-center gap-2 rounded-lg bg-white px-8 py-4 font-semibold text-blue-600 transition-colors hover:bg-blue-50"
				>
					<FileText class="h-5 w-5" />
					Upload CV (Developers)
				</a>
				<a
					href="/hire"
					class="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-500 px-8 py-4 font-semibold text-white transition-colors hover:bg-blue-400"
				>
					<Briefcase class="h-5 w-5" />
					Post Job (Employers)
				</a>
			</div>
			<p class="mt-6 flex items-center justify-center gap-2 text-sm text-blue-200">
				<DollarSign class="h-4 w-4" /> $0 forever • No credit card required • No hidden fees
			</p>
		</div>
	</section>
</div>
